import { API_BASE } from "../config";
import { formatDateOnly } from '../utils/dates';
import React, { useState, useEffect, useCallback } from 'react';
import { PantryItemForm } from './PantryForms';
import UhcCompatBadge from './UhcCompatBadge';
import InventoryManager from './InventoryManager';
import PantryItemViewModal from './PantryItemViewModal';
import VisionGroceryCapture from './VisionGroceryCapture';
import ListControls from './ListControls';
import Pagination from './Pagination';
import { useConfirm } from './ConfirmProvider';

const PantryDetailView = ({ pantry, onBack, fetchWithAuth }) => {
  const confirm = useConfirm();
  const [pantryData, setPantryData] = useState(pantry);
  const [showItemForm, setShowItemForm] = useState(false);
  const [showScanModal, setShowScanModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [viewingItem, setViewingItem] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [isDeleting, setIsDeleting] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState('items');

  // Paginated items state
  const [items, setItems] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [perPage, setPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsLoading, setItemsLoading] = useState(false);

  // Load pantry metadata (name, description, location)
  useEffect(() => {
    loadPantryData();
  }, [pantry.id]);

  // Load paginated items
  useEffect(() => {
    if (activeTab === 'items') {
      loadItems();
    }
  }, [pantry.id, refreshKey, searchQuery, sortBy, perPage, currentPage, activeTab]);

  const loadPantryData = async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/pantry/${pantry.id}`);
      if (response.ok) {
        const data = await response.json();
        setPantryData(data);
      }
    } catch (error) {
      console.error('Failed to load pantry data:', error);
    }
  };

  const loadItems = async () => {
    setItemsLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.set('search', searchQuery);
      params.set('sort', sortBy);
      if (perPage > 0) {
        params.set('limit', perPage);
        params.set('offset', (currentPage - 1) * perPage);
      } else {
        params.set('limit', 200);
        params.set('offset', 0);
      }
      const response = await fetchWithAuth(`${API_BASE}/api/pantry/${pantry.id}/items?${params}`);
      if (response.ok) {
        const data = await response.json();
        setItems(data.items || []);
        setTotalItems(data.total || 0);
      }
    } catch (error) {
      console.error('Failed to load items:', error);
    } finally {
      setItemsLoading(false);
    }
  };

  const forceRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  const handleAddItem = () => {
    setEditingItem(null);
    setShowItemForm(true);
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setShowItemForm(true);
  };

  const handleViewItem = (item) => {
    setViewingItem(item);
  };

  const handleDeleteItem = async (itemId) => {
    if (await confirm({
      title: 'Delete this pantry item?',
      message: 'It will be removed from your pantry and will no longer be used for recipe matching or safety checks.',
      confirmLabel: 'Delete Item',
      destructive: true,
    })) {
      try {
        const response = await fetchWithAuth(`${API_BASE}/api/pantry/${pantry.id}/items/${itemId}`, {
          method: 'DELETE'
        });
        if (response.ok) {
          forceRefresh();
        } else {
          alert('Failed to delete item');
        }
      } catch (error) {
        console.error('Failed to delete item:', error);
        alert('Failed to delete item');
      }
    }
  };

  const handleSelectItem = (itemId) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedItems.size === items.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(items.map(item => item.id)));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedItems.size === 0) return;
    if (!(await confirm({
      title: 'Delete selected items?',
      message: `${selectedItems.size} item${selectedItems.size === 1 ? '' : 's'} will be removed from this pantry. This cannot be undone.`,
      confirmLabel: `Delete ${selectedItems.size} Item${selectedItems.size === 1 ? '' : 's'}`,
      destructive: true,
    }))) return;

    setIsDeleting(true);
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/pantry/${pantry.id}/items/bulk`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(Array.from(selectedItems))
      });
      if (response.ok) {
        setSelectedItems(new Set());
        forceRefresh();
      } else {
        alert('Failed to delete items');
      }
    } catch (error) {
      console.error('Failed to delete items:', error);
      alert('Failed to delete items');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteAll = async () => {
    if (totalItems === 0) return;
    // requireText keeps the type-to-confirm safeguard the window.prompt had, but the modal
    // now DISABLES the action until the phrase matches — rather than accepting anything and
    // scolding the user afterwards with a second native alert.
    if (!(await confirm({
      title: 'Delete everything in this pantry?',
      message: `This permanently deletes ALL ${totalItems} item${totalItems === 1 ? '' : 's'} from this pantry. This cannot be undone.`,
      confirmLabel: 'Delete All Items',
      destructive: true,
      requireText: 'DELETE ALL',
    }))) return;

    setIsDeleting(true);
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/pantry/${pantry.id}/items/all?confirm=true`, {
        method: 'DELETE'
      });
      if (response.ok) {
        const result = await response.json();
        setSelectedItems(new Set());
        forceRefresh();
        alert(`Successfully deleted ${result.deleted_count} items.`);
      } else {
        alert('Failed to delete all items');
      }
    } catch (error) {
      console.error('Failed to delete all items:', error);
      alert('Failed to delete all items');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleItemSaved = () => {
    forceRefresh();
    setShowItemForm(false);
    setEditingItem(null);
  };

  const handleCloseItemForm = () => {
    setShowItemForm(false);
    setEditingItem(null);
  };

  const handleSearchChange = (val) => {
    setSearchQuery(val);
    setCurrentPage(1);
  };

  const handleSortChange = (val) => {
    setSortBy(val);
    setCurrentPage(1);
  };

  const handlePerPageChange = (val) => {
    setPerPage(val);
    setCurrentPage(1);
  };

  // Check if item is expiring soon (within 7 days)
  const isExpiringSoon = (expirationDate) => {
    if (!expirationDate) return false;
    const expDate = new Date(expirationDate);
    const today = new Date();
    const diffTime = expDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 7 && diffDays >= 0;
  };

  // Check if item is expired
  const isExpired = (expirationDate) => {
    if (!expirationDate) return false;
    const expDate = new Date(expirationDate);
    const today = new Date();
    return expDate < today;
  };

  const totalPages = perPage > 0 ? Math.ceil(totalItems / perPage) : 1;

  const sortOptions = [
    { value: 'newest', label: 'Most Recent' },
    { value: 'oldest', label: 'Oldest' },
    { value: 'expiring', label: 'Expiring Soon' },
    { value: 'alpha', label: 'Alphabetical (A-Z)' }
  ];

  return (
    <div className="page-layout">
      <div className="page-sticky-nav">
        <div className="page-header-row">
          <h2 className="page-section-title">Pantry Management — {pantryData.name}</h2>
          <button className="page-exit-btn" onClick={onBack}>← Nutrition</button>
        </div>
        <div className="page-nav-bar">
          <div className="pill-toolbar">
            <button className="pill-btn" onClick={handleAddItem}>
              Add Items Manually
            </button>
            <button className="pill-btn" onClick={() => setShowScanModal(true)}>
              Add Items via Scan
            </button>
            {selectedItems.size > 0 && (
              <button
                className="pill-btn pdv-deleteSelectedBtn"
                onClick={handleDeleteSelected}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : `Delete Selected (${selectedItems.size})`}
              </button>
            )}
            {totalItems > 0 && (
              <button
                className="pill-btn pdv-deleteAllBtn"
                onClick={handleDeleteAll}
                disabled={isDeleting}
              >
                Delete All
              </button>
            )}
          </div>
          <div className="tab-bar">
            <button
              className={`tab-item${activeTab === 'items' ? ' tab-item--active' : ''}`}
              onClick={() => setActiveTab('items')}
            >
              Pantry Items
            </button>
            <button
              className={`tab-item${activeTab === 'inventory' ? ' tab-item--active' : ''}`}
              onClick={() => setActiveTab('inventory')}
            >
              Inventory Management
            </button>
          </div>
        </div>
      </div>

      <div className="page-scroll-content">
        {(pantryData.description || pantryData.location) && (
          <div className="pdv-itemMetaSection">
            {pantryData.description && (
              <p className="pdv-descriptionText">{pantryData.description}</p>
            )}
            {pantryData.location && (
              <p className="pdv-locationText">
                Location: {pantryData.location}
              </p>
            )}
          </div>
        )}

        {/* Pantry Items Tab */}
        {activeTab === 'items' && (
          <>
            {/* List Controls */}
            <ListControls
              searchValue={searchQuery}
              onSearchChange={handleSearchChange}
              searchPlaceholder="Search items..."
              sortValue={sortBy}
              onSortChange={handleSortChange}
              sortOptions={sortOptions}
              perPage={perPage}
              onPerPageChange={handlePerPageChange}
              totalItems={totalItems}
            />

            <div className="pdv-itemsListContainer">
              <div className="pdv-listHeaderRow">
                <h4 className="pdv-itemsCountHeading">
                  Items in Pantry ({totalItems})
                </h4>
                {items.length > 0 && (
                  <label className="pdv-selectAllLabel">
                    <input
                      type="checkbox"
                      checked={items.length > 0 && selectedItems.size === items.length}
                      onChange={handleSelectAll}
                      className="pdv-selectAllCheckbox"
                    />
                    Select All
                  </label>
                )}
              </div>

              {itemsLoading ? (
                <div className="pdv-emptyStateContainer">
                  <p className="pdv-emptyStateMessage">Loading items...</p>
                </div>
              ) : items.length === 0 ? (
                <div className="pdv-emptyStateContainer">
                  <p className="pdv-emptyStateMessage">
                    {searchQuery ? 'No items match your search.' : 'No items in this pantry yet. Add some items to get started!'}
                  </p>
                </div>
              ) : (
                <div>
                  {items.map(item => (
                    <div key={item.id} className="objective-item pdv-itemCard" style={{ border: isExpired(item.expiration_date) ? '2px solid #EF4444' :
                             isExpiringSoon(item.expiration_date) ? '2px solid #F59E0B' :
                             '1px solid #E5E7EB', backgroundColor: isExpired(item.expiration_date) ? '#FEF2F2' :
                                      isExpiringSoon(item.expiration_date) ? '#FFFBEB' :
                                      'white' }}>
                      {/* Selection Checkbox */}
                      <div className="pdv-itemCheckboxCell">
                        <input
                          type="checkbox"
                          checked={selectedItems.has(item.id)}
                          onChange={() => handleSelectItem(item.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="pdv-itemCheckbox"
                        />
                      </div>
                      <div className="objective-content pdv-itemContent">
                        <div className="pdv-itemTitleRow">
                          <h4 className="pdv-itemNameHeading">
                            {item.name}
                          </h4>
                          {item.brand && (
                            <span className="pdv-itemBrandSpan">
                              by {item.brand}
                            </span>
                          )}
                        </div>
                        <UhcCompatBadge compatibility={item.uhc_compatibility} />

                        <div className="pdv-itemDetailsRow1">
                          <span className="pdv-quantitySpan">
                            Quantity: {typeof item.quantity === 'number' ? parseFloat(item.quantity.toFixed(2)) : item.quantity} {item.unit || ''}
                          </span>
                          {item.category && (
                            <span>Category: {item.category}</span>
                          )}
                          {item.location_in_pantry && (
                            <span>Location: {item.location_in_pantry}</span>
                          )}
                          {item.barcode && (
                            <span>Barcode: {item.barcode}</span>
                          )}
                        </div>

                        <div className="pdv-itemDetailsRow2">
                          {item.purchase_date && (
                            <span className="pdv-purchaseDateSpan">
                              Purchased: {formatDateOnly(item.purchase_date)}
                            </span>
                          )}

                          {item.expiration_date && (
                            <span className="pdv-expirationBadge" style={{ backgroundColor: isExpired(item.expiration_date) ? '#FEE2E2' :
                                              isExpiringSoon(item.expiration_date) ? '#FEF3C7' :
                                              '#D1FAE5', color: isExpired(item.expiration_date) ? '#991B1B' :
                                     isExpiringSoon(item.expiration_date) ? '#92400E' :
                                     '#065F46' }}>
                              {isExpired(item.expiration_date) ? 'Expired' :
                               isExpiringSoon(item.expiration_date) ? 'Expires Soon' :
                               'Expires'}: {formatDateOnly(item.expiration_date)}
                            </span>
                          )}

                          {item.is_frozen && (
                            <span className="pdv-frozenBadge">
                              Frozen
                            </span>
                          )}

                          {item.is_opened && (
                            <span className="pdv-openedBadge">
                              Opened
                            </span>
                          )}

                          {item.storage_method && (
                            <span className="pdv-storageBadge">
                              {item.is_frozen ? 'Freezer' : item.storage_method === 'pantry' ? 'Pantry' : item.storage_method === 'freezer' ? 'Freezer' : 'Refrigerator'}
                            </span>
                          )}

                          {item.out_of_stock && (
                            <span className="pdv-outOfStockBadge">
                              Out of Stock
                            </span>
                          )}

                          {item.is_low_stock && !item.out_of_stock && (
                            <span className="pdv-lowStockBadge">
                              Low Stock (below {item.minimum_quantity} {item.unit})
                            </span>
                          )}
                        </div>

                        {item.notes && (
                          <div className="pdv-itemNotesSection">
                            {item.notes}
                          </div>
                        )}
                      </div>

                      <div className="objective-actions">
                        <button
                          className="btn btn-primary btn-sm pdv-viewItemBtn"
                          onClick={() => handleViewItem(item)}
                        >
                          View
                        </button>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleEditItem(item)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDeleteItem(item.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pagination */}
            {perPage > 0 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            )}
          </>
        )}

        {/* Inventory Management Tab */}
        {activeTab === 'inventory' && (
          <div className="pdv-inventoryManagerContainer">
            <InventoryManager
              token={localStorage.getItem('token')}
              pantryId={pantryData.id}
              onRefreshPantry={forceRefresh}
              showAsCard={false}
            />
          </div>
        )}
      </div>

      {/* Item Form */}
      {showItemForm && (
        <PantryItemForm
          onClose={handleCloseItemForm}
          onSave={handleItemSaved}
          fetchWithAuth={fetchWithAuth}
          editingItem={editingItem}
          pantryId={pantryData.id}
        />
      )}

      {/* Vision Grocery Capture Modal */}
      {showScanModal && (
        <VisionGroceryCapture
          token={localStorage.getItem('token')}
          fetchWithAuth={fetchWithAuth}
          defaultDestination="pantry"
          defaultPantryId={pantryData.id}
          hideDestinationSelector={true}
          onItemsConfirmed={(items, destination) => {
            forceRefresh();
          }}
          onClose={() => setShowScanModal(false)}
        />
      )}

      {/* Item View Modal */}
      {viewingItem && (
        <PantryItemViewModal
          item={viewingItem}
          onClose={() => setViewingItem(null)}
          fetchWithAuth={fetchWithAuth}
        />
      )}
    </div>
  );
}
export default PantryDetailView;
